import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// @mui
import Fab from '@mui/material/Fab';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import CardHeader from '@mui/material/CardHeader';
import Button from '@mui/material/Button';
import { Timeline, timelineItemClasses } from '@mui/lab';
import { Avatar, CardContent, Chip, Tab, Tabs, tabsClasses } from '@mui/material';
// utils
import { fNumber } from 'src/utils/format-number';
// types
import { useAuthContext } from 'src/auth/hooks';
// components
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
import Iconify from 'src/components/iconify';
import FormProvider, { RHFAutocomplete, RHFTextField } from 'src/components/hook-form';
import Editor from 'src/components/editor';
//
import { setError } from 'src/redux/slices/error';
import EventItem from './event-item';
import {
  AccountDto,
  CommentDto,
  MailsService,
  MemberEventDto,
  MemberEventType,
  MembersService,
  MemberStatsDto,
  TextMessageService,
} from '../../api';
import { useLocales } from '../../locales';
import { getMemberComments, getMemberEvents } from '../../redux/slices/members';
import { useDispatch, useSelector } from '../../redux/store';
import { toBase64 } from '../../utils/files';
import { fDate } from '../../utils/format-time';
import { paths } from '../../routes/paths';
import ProfileCommentItem from './profile-comment-item';
import SubAccountList from './sub-accounts/sub-account-list';

// ----------------------------------------------------------------------

type Props = {
  member: AccountDto;
  events: MemberEventDto[];
  comments: CommentDto[];
  stats: MemberStatsDto;
};

export default function ProfileHome({ member, events, stats, comments }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);

  const selectedGym = useSelector((state) => state.gym.globalGym);

  const { user } = useAuthContext();

  const { t } = useLocales();

  const newCommentSchema = Yup.object().shape({
    body: Yup.string().required(t('Body is required')),
    file: Yup.mixed(),
  });

  const commentDefaultValues = useMemo(
    () => ({
      body: '',
      file: '',
    }),
    []
  );

  const commentMethods = useForm({
    resolver: yupResolver(newCommentSchema),
    defaultValues: commentDefaultValues,
  });

  const newTextMessageSchema = Yup.object().shape({
    recipientId: Yup.number(),
    gymId: Yup.number(),
    body: Yup.string().required(t('Body is required')),
  });

  const textMessageDefaultValues = useMemo(
    () => ({
      recipientId: member.id,
      body: '',
      gymId: selectedGym.id,
    }),
    [selectedGym, member]
  );

  const textMessageMethods = useForm({
    resolver: yupResolver(newTextMessageSchema),
    defaultValues: textMessageDefaultValues,
  });

  const newEmailSchema = Yup.object().shape({
    body: Yup.string().required(t('Body is required')),
    subject: Yup.string().required(t('Subject is required')),
    from: Yup.string().required(),
    sendTo: Yup.array().required().min(1),
  });

  const members = useMemo(() => [member], [member]);

  const emailDefaultValues = useMemo(
    () => ({
      body: '',
      subject: '',
      from: user?.email ?? '',
      sendTo: members,
    }),
    [user, members]
  );

  const emailMethods = useForm({
    resolver: yupResolver(newEmailSchema),
    defaultValues: emailDefaultValues,
  });

  useEffect(() => emailMethods.reset(), [member, emailMethods]);

  const onEmailStateChange = (value: string, _delta: any, _source: any, _editor: any) => {
    emailMethods.setValue('body', value);
  };

  const emailContent = emailMethods.watch('body');

  const handleAttach = () => {
    if (fileRef.current) {
      fileRef.current.click();
    }
  };

  const dispatch = useDispatch();

  const onCommentSubmit = commentMethods.handleSubmit(async (data) => {
    try {
      if ((fileRef.current?.files?.length ?? 0) > 0) {
        const file = fileRef.current!.files![0];

        const fileData = await toBase64(file);

        await MembersService.createComment({
          id: member.id!,
          body: {
            ...data,
            fileData: fileData?.toString(),
          },
        });
      } else {
        await MembersService.createComment({
          id: member.id!,
          body: data as any,
        });
      }

      commentMethods.reset();
      enqueueSnackbar(t('Note created!'));

      dispatch(getMemberEvents(member.id!));
      dispatch(getMemberComments(member.id!));
    } catch (error) {
      dispatch(setError(error));
    }
  });

  const onEmailSubmit = emailMethods.handleSubmit(async (data) => {
    try {
      await MailsService.sendEmail({
        body: {
          html: data.body,
          subject: data.subject,
          sendTo: data.sendTo.map((m) => m.email!),
          from: data.from,
        },
      });

      emailMethods.reset();
      enqueueSnackbar(t('Email sent!'));

      dispatch(getMemberEvents(member.id!));
      dispatch(getMemberComments(member.id!));
    } catch (error) {
      dispatch(setError(error));
    }
  });

  const onTextMessageSubmit = textMessageMethods.handleSubmit(async (data) => {
    try {
      await TextMessageService.create({
        body: { ...(data as any), recipientId: member.id, gymId: selectedGym.id },
      });

      textMessageMethods.reset();
      enqueueSnackbar(t('Text message sent!'));

      dispatch(getMemberEvents(member.id!));
      dispatch(getMemberComments(member.id!));
    } catch (error) {
      dispatch(setError(error));
    }
  });

  const [selectedEvents, setSelectedEvents] = useState([...events]);

  const sortedComments = [...comments];
  sortedComments.sort((a, b) => b.createdDate!.toMillis() - a.createdDate!.toMillis());

  const tabs = [
    {
      value: 'note',
      label: t('Note'),
      icon: <Iconify icon="mdi:note-text-outline" width={24} />,
    },
    {
      value: 'sms',
      label: t('SMS'),
      icon: <Iconify icon="material-symbols:sms-outline" width={24} />,
    },
    {
      value: 'email',
      label: t('Email'),
      icon: <Iconify icon="mdi:email-outline" width={24} />,
    },
  ];

  const [currentTab, setCurrentTab] = useState(tabs[0].value);

  const handleChangeTab = useCallback((_event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const timeLineTabs = [
    {
      value: 'all',
      label: t('All ({{count}})', { count: events.length }),
    },
    {
      value: MemberEventType.Email,
      label: t('Emails ({{count}})', {
        count: events.filter((x) => x.type === MemberEventType.Email).length,
      }),
      icon: <Iconify icon="mdi:email-outline" width={24} />,
    },
    {
      value: MemberEventType.Sms,
      label: t('SMS ({{count}})', {
        count: events.filter((x) => x.type === MemberEventType.Sms).length,
      }),
      icon: <Iconify icon="material-symbols:sms-outline" width={24} />,
    },
    {
      value: MemberEventType.Notification,
      label: t('Notifications ({{count}})', {
        count: events.filter((x) => x.type === MemberEventType.Notification).length,
      }),
      icon: <Iconify icon="material-symbols:notifications-outline" width={24} />,
    },
    {
      value: MemberEventType.Comment,
      label: t('Notes ({{count}})', {
        count: events.filter((x) => x.type === MemberEventType.Comment).length,
      }),
      icon: <Iconify icon="mdi:note-text-outline" width={24} />,
    },
  ];

  const [currentTimeLineTab, setTimeLineCurrentTab] = useState(timeLineTabs[0].value);

  const handleTimeLineChangeTab = useCallback((_event: React.SyntheticEvent, newValue: string) => {
    setTimeLineCurrentTab(newValue);
  }, []);

  useEffect(() => {
    if (currentTimeLineTab === MemberEventType.Notification) {
      setSelectedEvents([...events].filter((x) => x.type === MemberEventType.Notification));
    } else if (currentTimeLineTab === MemberEventType.Email) {
      setSelectedEvents([...events].filter((x) => x.type === MemberEventType.Email));
    } else if (currentTimeLineTab === MemberEventType.Sms) {
      setSelectedEvents([...events].filter((x) => x.type === MemberEventType.Sms));
    } else if (currentTimeLineTab === MemberEventType.Comment) {
      setSelectedEvents([...events].filter((x) => x.type === MemberEventType.Comment));
    } else {
      setSelectedEvents([...events]);
    }
  }, [currentTimeLineTab, events, setSelectedEvents]);

  const renderStats = (
    <Card sx={{ py: 3, textAlign: 'center', typography: 'h4' }}>
      <Stack
        direction="row"
        divider={<Divider orientation="vertical" flexItem sx={{ borderStyle: 'dashed' }} />}
      >
        <Stack width={1}>
          {fNumber(stats.numberOfClassRegistrations ?? 0)}
          <Box component="span" sx={{ color: 'text.secondary', typography: 'body2' }}>
            {t('Participations')}
          </Box>
        </Stack>

        <Stack width={1}>
          {fNumber(stats.numberOfTrainings ?? 0)}
          <Box component="span" sx={{ color: 'text.secondary', typography: 'body2' }}>
            {t('Trainings')}
          </Box>
        </Stack>
      </Stack>
    </Card>
  );

  const renderAbout = (
    <Card>
      <CardHeader title={t('About')} />

      <Stack spacing={2} sx={{ p: 3 }}>
        {member.address != null && (
          <Stack direction="row" sx={{ typography: 'body2' }}>
            <Iconify icon="mdi:location-on-outline" sx={{ mr: 2 }} />
            {`${member.address?.addressLine}`} <br />
            {`${member.address?.postalNumber} ${member.address?.city}`}
            <br />
            {`${member.address?.country}`}
          </Stack>
        )}

        <Stack direction="row" sx={{ typography: 'body2' }}>
          <Iconify icon="mdi:email-outline" width={24} sx={{ mr: 2 }} />
          {member.email}
        </Stack>

        <Stack direction="row" sx={{ typography: 'body2' }}>
          <Iconify icon="mdi:phone-outline" width={24} sx={{ mr: 2 }} />
          {member.phoneNumber}
        </Stack>

        {member.dateOfBirth && (
          <Stack direction="row" sx={{ typography: 'body2' }}>
            <Iconify icon="mdi:cake" width={24} sx={{ mr: 2 }} />
            {fDate(member.dateOfBirth!, 'PPP')}
          </Stack>
        )}

        {member.primaryAccount && (
          <Stack direction="row" sx={{ typography: 'body2' }}>
            <Link href={paths.members.view(member.primaryAccount!.id!)}>
              <Stack direction="row" sx={{ typography: 'body2' }}>
                <Iconify icon="raphael:parent" width={24} sx={{ mr: 2 }} />
                {member.primaryAccount?.fullName}
              </Stack>
            </Link>
          </Stack>
        )}
      </Stack>
    </Card>
  );

  const renderEditor = (
    <FormProvider methods={emailMethods} onSubmit={onEmailSubmit}>
      <Stack spacing={2}>
        <RHFTextField name="from" label={t('From')} disabled />

        <RHFAutocomplete
          key={`recipients-${member.id}`}
          name="sendTo"
          multiple
          disabled
          options={members}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.fullName ?? '')}
          isOptionEqualToValue={(option, value) => option.email === value.email}
          label={`${t('To')}`}
          renderValue={(value, getItemProps) => (
            <>
              {value.map((v) =>
                typeof v === 'string' ? (
                  v
                ) : (
                  <Chip
                    key={`chip-recipients-${v.id}`}
                    avatar={<Avatar alt={v.fullName} src={v.profileImageUrl} />}
                    label={v.fullName}
                    variant="outlined"
                  />
                )
              )}
            </>
          )}
        />

        <RHFTextField name="subject" label={t('Subject')} />

        <Editor simple id="reply-mail" value={emailContent} onChange={onEmailStateChange} />

        <Stack direction="row" alignItems="end" sx={{ width: 1 }} justifyContent="end">
          <Button
            type="submit"
            variant="contained"
            color="primary"
            endIcon={<Iconify icon="mdi:send" />}
            loading={emailMethods.formState.isSubmitting}
          >
            {t('Send')}
          </Button>
        </Stack>
      </Stack>
    </FormProvider>
  );

  const renderInput = (
    <Card>
      <Tabs value={currentTab} onChange={handleChangeTab}>
        {tabs.map((tab) => (
          <Tab
            key={tab.value}
            value={tab.value}
            icon={tab.icon}
            label={tab.label}
            sx={{
              pr: 2,
              pl: 2,
            }}
          />
        ))}
      </Tabs>
      <CardContent>
        {currentTab === 'note' && (
          <FormProvider methods={commentMethods} onSubmit={onCommentSubmit}>
            <RHFTextField
              name="body"
              multiline
              fullWidth
              rows={4}
              placeholder={t('Share what you are thinking here...')}
              sx={{
                mb: 3,
              }}
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ color: 'text.secondary' }}
              >
                <Fab size="small" color="inherit" variant="softExtended" onClick={handleAttach}>
                  <Iconify
                    icon="solar:gallery-wide-bold"
                    width={24}
                    sx={{ color: 'success.main' }}
                  />
                  {t('File')}
                </Fab>
              </Stack>

              <Button
                type="submit"
                variant="contained"
                color="primary"
                loading={commentMethods.formState.isSubmitting}
              >
                {t('Add')}
              </Button>
            </Stack>

            <input ref={fileRef} type="file" style={{ display: 'none' }} />
          </FormProvider>
        )}

        {currentTab === 'sms' && (
          <FormProvider methods={textMessageMethods} onSubmit={onTextMessageSubmit}>
            <RHFTextField
              name="body"
              label={t('Text message')}
              multiline
              fullWidth
              rows={4}
              placeholder={t('Share what you are thinking here...')}
              sx={{
                mb: 3,
              }}
            />
            <Stack direction="row" alignItems="end" justifyContent="end">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                loading={textMessageMethods.formState.isSubmitting}
                endIcon={<Iconify icon="mdi:send" />}
              >
                {t('Send')}
              </Button>
            </Stack>
          </FormProvider>
        )}

        {currentTab === 'email' && renderEditor}
      </CardContent>
    </Card>
  );

  const renderTimeLine = (
    <Stack spacing={3}>
      <Tabs
        value={currentTimeLineTab}
        onChange={handleTimeLineChangeTab}
        sx={{
          [`& .${tabsClasses.flexContainer}`]: {
            justifyContent: {
              sm: 'start',
            },
          },
        }}
      >
        {timeLineTabs.map((tab) => (
          <Tab key={tab.value} value={tab.value} icon={tab.icon} label={tab.label} />
        ))}
      </Tabs>
      <Timeline
        sx={{
          p: 0,
          m: 0,
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
      >
        {selectedEvents
          .sort((a, b) => b.timestamp!.toMillis() - a.timestamp!.toMillis())
          .map((event, index) => {
            const lastTimeline = index === selectedEvents.length - 1;
            return (
              <EventItem
                key={`${event.title}-${event.timestamp}`}
                event={event}
                member={member}
                isLast={lastTimeline}
              />
            );
          })}
      </Timeline>
    </Stack>
  );

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Stack spacing={3}>
          {renderStats}

          {renderAbout}

          {(member.subAccounts?.length ?? 0) > 0 && (
            <SubAccountList subAccounts={member.subAccounts ?? []} />
          )}

          {sortedComments.map((event) => (
            <ProfileCommentItem
              key={`${event.title}-${event.createdDate}`}
              comment={event}
              member={member}
            />
          ))}
        </Stack>
      </Grid>

      <Grid size={{ xs: 12, md: 8 }}>
        <Stack spacing={3}>
          {renderInput}

          {renderTimeLine}
        </Stack>
      </Grid>
    </Grid>
  );
}
